/**
 * useServerFetch — triggers server method call when a configured trigger field
 * changes in a child table row, then patches the row with the response.
 *
 * Key design decisions:
 * - Lives at ChildTableField level (not per-row) to avoid hooks-in-loops
 * - Uses requestId staleness pattern (not AbortController) because
 *   getERPClient().call() doesn't accept a signal parameter
 * - 150ms shimmer delay: skip loading indicator for fast responses
 * - 10s timeout: treat as failure, show inline warning
 * - Never overwrites trigger field (item_code) or protected system fields
 */

import { useState, useRef, useCallback, useEffect } from "react";
import { getERPClient } from "@/lib/erp/client";
import { PROTECTED_FIELDS } from "./serverFetchConfig";
import type { ServerFetchMapping, ServerFetchState, ChildTableRow } from "./types";

interface UseServerFetchProps {
  config: ServerFetchMapping[] | undefined;
  parentDoc: Record<string, any> | undefined;
  parentDoctype: string | undefined;
  updateRow: (index: number, patch: Partial<ChildTableRow>) => void;
  rows: ChildTableRow[];
}

interface UseServerFetchReturn {
  /** Wraps onRowChange — detects trigger field, fires fetch, calls updateRow */
  handleRowChange: (index: number, patch: Partial<ChildTableRow>) => void;
  /** Per-row fetch state keyed by row identifier (_localId or name) */
  fetchStates: Map<string, ServerFetchState>;
}

/** Get a stable identifier for a row (survives add/delete index shifts) */
function getRowId(row: ChildTableRow | undefined): string {
  return row?.name || row?._localId || "";
}

/** Filter server response to remove protected fields and the trigger field */
function filterResponse(
  response: Record<string, any>,
  triggerField: string
): Record<string, any> {
  const filtered: Record<string, any> = {};
  for (const [key, value] of Object.entries(response)) {
    if (PROTECTED_FIELDS.has(key)) continue;
    if (key === triggerField) continue;
    // Skip internal Frappe fields
    if (key.startsWith("__")) continue;
    // Only patch non-null values from the server
    if (value !== null && value !== undefined) {
      filtered[key] = value;
    }
  }
  return filtered;
}

const FETCH_TIMEOUT_MS = 10_000;
const SHIMMER_DELAY_MS = 150;

export function useServerFetch({
  config,
  parentDoc,
  parentDoctype,
  updateRow,
  rows,
}: UseServerFetchProps): UseServerFetchReturn {
  // Per-row monotonic request counter for staleness detection
  const requestIds = useRef<Map<string, number>>(new Map());
  // Per-row shimmer delay timers
  const shimmerTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(
    new Map()
  );
  // Per-row tracking of which fields were set by server fetch (for clear-on-empty)
  const serverPopulatedFields = useRef<Map<string, Set<string>>>(new Map());
  // Per-row fetch state (loading + error)
  const [fetchStates, setFetchStates] = useState<Map<string, ServerFetchState>>(
    () => new Map()
  );

  // Stable ref for rows so the callback doesn't close over stale rows
  const rowsRef = useRef(rows);
  rowsRef.current = rows;

  // Stable ref for parentDoc
  const parentDocRef = useRef(parentDoc);
  parentDocRef.current = parentDoc;

  // Stable ref for parentDoctype
  const parentDoctypeRef = useRef(parentDoctype);
  parentDoctypeRef.current = parentDoctype;

  // Stable ref for fetchStates so handleRowChange doesn't close over stale state
  const fetchStatesRef = useRef(fetchStates);
  fetchStatesRef.current = fetchStates;

  // Helper to update fetch state for a single row
  const setRowFetchState = useCallback(
    (rowId: string, state: ServerFetchState) => {
      setFetchStates((prev) => {
        const next = new Map(prev);
        if (!state.loading && !state.error) {
          next.delete(rowId);
        } else {
          next.set(rowId, state);
        }
        return next;
      });
    },
    []
  );

  // Clear a shimmer timer for a row
  const clearShimmerTimer = useCallback((rowId: string) => {
    const timer = shimmerTimers.current.get(rowId);
    if (timer) {
      clearTimeout(timer);
      shimmerTimers.current.delete(rowId);
    }
  }, []);

  // Main handler: wraps onRowChange with server fetch logic
  const handleRowChange = useCallback(
    (index: number, patch: Partial<ChildTableRow>) => {
      // Always apply the user's change immediately
      updateRow(index, patch);

      // If no config, nothing to fetch
      if (!config || config.length === 0) return;

      // Check if the patch contains a trigger field
      for (const mapping of config) {
        const triggerValue = patch[mapping.triggerField];
        if (triggerValue === undefined) continue;

        // Found a trigger field in the patch
        const currentRow = rowsRef.current[index];
        const rowId = getRowId(currentRow);
        if (!rowId) continue;

        // If trigger value is empty, clear fetch state and server-populated fields
        if (
          triggerValue === null ||
          triggerValue === ""
        ) {
          clearShimmerTimer(rowId);
          setRowFetchState(rowId, { loading: false, error: null });

          // Clear fields that were previously set by server fetch
          const populatedFields = serverPopulatedFields.current.get(rowId);
          if (populatedFields && populatedFields.size > 0) {
            const clearPatch: Record<string, any> = {};
            for (const fieldName of populatedFields) {
              clearPatch[fieldName] = "";
            }
            updateRow(index, clearPatch);
            serverPopulatedFields.current.delete(rowId);
          }
          return;
        }

        // Check if value actually changed — unless there's an error (allow retry)
        const currentFetchState = fetchStatesRef.current.get(rowId);
        if (
          currentRow &&
          currentRow[mapping.triggerField] === triggerValue &&
          !currentFetchState?.error
        ) {
          return;
        }

        // Increment request counter for this row (staleness guard)
        const prevId = requestIds.current.get(rowId) || 0;
        const thisRequestId = prevId + 1;
        requestIds.current.set(rowId, thisRequestId);

        // Clear previous shimmer timer
        clearShimmerTimer(rowId);

        // Start 150ms shimmer delay — only show loading if fetch takes > 150ms
        const timer = setTimeout(() => {
          // Only show shimmer if this request is still current
          if (requestIds.current.get(rowId) === thisRequestId) {
            setRowFetchState(rowId, { loading: true, error: null });
          }
        }, SHIMMER_DELAY_MS);
        shimmerTimers.current.set(rowId, timer);

        // Build server call args
        const mergedRow = { ...currentRow, ...patch };
        let innerArgs: Record<string, any>;

        if (mapping.buildArgs) {
          innerArgs = mapping.buildArgs(
            triggerValue as string,
            mergedRow,
            parentDocRef.current || {},
            parentDoctypeRef.current || ""
          );
        } else {
          // Legacy path: simple parentContextFields iteration (backward compat)
          const legacyArgs: Record<string, any> = {
            item_code: triggerValue,
            doctype: parentDoctypeRef.current || "Stock Entry",
          };
          for (const field of mapping.parentContextFields) {
            const val = parentDocRef.current?.[field];
            if (val !== undefined && val !== null && val !== "") {
              legacyArgs[field] = val;
            }
          }
          // Add row context (warehouse)
          if (mergedRow.s_warehouse) {
            legacyArgs.warehouse = mergedRow.s_warehouse;
          } else if (mergedRow.t_warehouse) {
            legacyArgs.warehouse = mergedRow.t_warehouse;
          }
          innerArgs = { args: legacyArgs };
        }

        // Fire the server call
        const doFetch = async () => {
          try {
            // Create a timeout race
            const timeoutPromise = new Promise<never>((_, reject) => {
              setTimeout(
                () => reject(new Error("Server fetch timed out")),
                FETCH_TIMEOUT_MS
              );
            });

            const fetchPromise = getERPClient().call<Record<string, any>>(
              mapping.method,
              innerArgs
            );

            const response = await Promise.race([fetchPromise, timeoutPromise]);

            // Staleness check: if another request fired for this row, discard
            if (requestIds.current.get(rowId) !== thisRequestId) {
              return;
            }

            // Clear shimmer timer and loading state
            clearShimmerTimer(rowId);

            // Filter response and patch the row
            const filteredPatch = filterResponse(response, mapping.triggerField);
            if (Object.keys(filteredPatch).length > 0) {
              updateRow(index, filteredPatch);
              // Track which fields were set by server (for clear-on-empty)
              serverPopulatedFields.current.set(
                rowId,
                new Set(Object.keys(filteredPatch))
              );
            }

            setRowFetchState(rowId, { loading: false, error: null });
          } catch (err) {
            // Staleness check
            if (requestIds.current.get(rowId) !== thisRequestId) {
              return;
            }

            clearShimmerTimer(rowId);

            const message =
              err instanceof Error ? err.message : "Failed to load item details";
            setRowFetchState(rowId, { loading: false, error: message });
          }
        };

        doFetch();

        // Only handle the first matching trigger per patch
        return;
      }
    },
    [config, updateRow, clearShimmerTimer, setRowFetchState]
  );

  // Cleanup on unmount: clear all timers
  useEffect(() => {
    return () => {
      for (const timer of shimmerTimers.current.values()) {
        clearTimeout(timer);
      }
      shimmerTimers.current.clear();
      requestIds.current.clear();
      serverPopulatedFields.current.clear();
    };
  }, []);

  return { handleRowChange, fetchStates };
}
